import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { useCallback } from "react";
import { v4 as UUID } from "uuid";

import {
  ASSIGN_WORK_ORDER_TO_FINANCE_QUOTE_MUTATION,
  ATTACH_FINANCE_QUOTE_PURCHASE_ORDER_MUTATION,
  CONVERT_FINANCE_QUOTE_TO_INVOICE_MUTATION,
  DELETE_FINANCE_QUOTE_MUTATION,
  DOWNLOAD_FINANCE_QUOTE_PDF_MUTATION,
  DUPLICATE_FINANCE_QUOTE_MUTATION,
  GET_FINANCE_QUOTE_QUERY,
  LIST_FINANCE_QUOTE_ACTIVITY_LOGS_QUERY,
  LIST_OEM_EMAIL_THREADS_BY_FINANCE_QUOTE_QUERY,
  REMOVE_FINANCE_QUOTE_PURCHASE_ORDER_MUTATION,
  SEND_FINANCE_QUOTE_MUTATION,
  UPDATE_FINANCE_QUOTE_MUTATION,
} from "~/api";
import type { FinanceQuote } from "~/types/finance";
import { errorsHandler } from "~/transformers";

const financeQuoteActivityLogsRefetch = (quoteId: string) => ({
  query: LIST_FINANCE_QUOTE_ACTIVITY_LOGS_QUERY,
  variables: {
    quoteId,
    params: { limit: 20, skip: 0, sort: ["created_at:desc"] },
  },
});

const financeQuoteDetailRefetches = (quoteId: string) => [
  { query: GET_FINANCE_QUOTE_QUERY, variables: { id: quoteId } },
  financeQuoteActivityLogsRefetch(quoteId),
];

const evictFinanceQuoteActivityLogs = (cache: {
  evict: (args: object) => void;
  gc: () => void;
}) => {
  cache.evict({ id: "ROOT_QUERY", fieldName: "listFinanceQuoteActivityLogs" });
  cache.gc();
};

export const useGetFinanceQuoteDetail = (id?: string) => {
  const { data, loading, error, refetch } = useQuery<{
    getFinanceQuote: FinanceQuote | null;
  }>(GET_FINANCE_QUOTE_QUERY, {
    skip: !id,
    variables: { id },
    fetchPolicy: "cache-and-network",
  });

  return {
    quote: data?.getFinanceQuote ?? null,
    loading,
    error,
    refetch,
  };
};

export const useUpdateFinanceQuote = () => {
  const [mutate, { loading }] = useMutation(UPDATE_FINANCE_QUOTE_MUTATION);

  return {
    updateFinanceQuote: (
      id: string,
      input: Record<string, unknown>,
    ): Promise<FinanceQuote> =>
      new Promise((resolve, reject) => {
        mutate({
          variables: { input: { _id: id, ...input } },
          refetchQueries: financeQuoteDetailRefetches(id),
          update: (cache) => {
            evictFinanceQuoteActivityLogs(cache);
          },
        })
          .then(({ data }) => resolve(data?.updateFinanceQuote))
          .catch((err) => reject(errorsHandler(err)));
      }),
    loading,
  };
};

export const useSendFinanceQuote = () => {
  const [mutate, { loading }] = useMutation(SEND_FINANCE_QUOTE_MUTATION);
  return {
    sendFinanceQuote: (input: Record<string, unknown>) =>
      new Promise<FinanceQuote>((resolve, reject) => {
        mutate({
          variables: { input },
          refetchQueries: financeQuoteDetailRefetches(input.quoteId as string),
          update: (cache) => {
            evictFinanceQuoteActivityLogs(cache);
          },
        })
          .then(({ data }) => resolve(data?.sendFinanceQuote))
          .catch((err) => reject(errorsHandler(err)));
      }),
    loading,
  };
};

export const useDownloadFinanceQuotePdf = () => {
  const [mutate, { loading }] = useMutation(
    DOWNLOAD_FINANCE_QUOTE_PDF_MUTATION,
  );
  return {
    downloadFinanceQuotePDF: async (quoteId: string) => {
      if (!quoteId) return { success: false as const };
      const uuid = UUID();
      sessionStorage.setItem(quoteId, uuid);
      try {
        await mutate({ variables: { quoteId, uuid } });
        return { success: true as const };
      } catch (err) {
        return { success: false as const, error: errorsHandler(err) };
      }
    },
    loading,
  };
};

export const useDuplicateFinanceQuote = () => {
  const [mutate, { loading }] = useMutation(DUPLICATE_FINANCE_QUOTE_MUTATION);
  return {
    duplicateFinanceQuote: (id: string) =>
      new Promise<{ _id: string; quoteNumber?: string }>((resolve, reject) => {
        mutate({ variables: { id } })
          .then(({ data }) => resolve(data?.duplicateFinanceQuote))
          .catch((err) => reject(errorsHandler(err)));
      }),
    loading,
  };
};

export const useDeleteFinanceQuote = () => {
  const [mutate, { loading }] = useMutation(DELETE_FINANCE_QUOTE_MUTATION);
  return {
    deleteFinanceQuote: (id: string) =>
      new Promise<void>((resolve, reject) => {
        mutate({ variables: { id } })
          .then(() => resolve())
          .catch((err) => reject(errorsHandler(err)));
      }),
    loading,
  };
};

export const useConvertFinanceQuoteToInvoice = () => {
  const [mutate, { loading }] = useMutation(
    CONVERT_FINANCE_QUOTE_TO_INVOICE_MUTATION,
  );
  return {
    convertFinanceQuoteToInvoice: (quoteId: string) =>
      new Promise<{ _id: string; invoiceNumber?: string }>(
        (resolve, reject) => {
          mutate({
            variables: { quoteId },
            refetchQueries: financeQuoteDetailRefetches(quoteId),
            update: (cache) => {
              evictFinanceQuoteActivityLogs(cache);
            },
          })
            .then(({ data }) => resolve(data?.convertFinanceQuoteToInvoice))
            .catch((err) => reject(errorsHandler(err)));
        },
      ),
    loading,
  };
};

export const useAssignWorkOrderToFinanceQuote = () => {
  const [mutate, { loading }] = useMutation(
    ASSIGN_WORK_ORDER_TO_FINANCE_QUOTE_MUTATION,
  );
  return {
    assignWorkOrderToFinanceQuote: (quoteId: string, workOrderId: string) =>
      new Promise<FinanceQuote>((resolve, reject) => {
        mutate({
          variables: { quoteId, workOrderId },
          refetchQueries: financeQuoteDetailRefetches(quoteId),
          awaitRefetchQueries: true,
          update: (cache) => {
            evictFinanceQuoteActivityLogs(cache);
          },
        })
          .then(({ data }) => resolve(data?.assignWorkOrderToFinanceQuote))
          .catch((err) => reject(errorsHandler(err)));
      }),
    loading,
  };
};

export const useListFinanceQuoteActivityLogs = (
  quoteId?: string,
  skip = false,
) => {
  const { data, loading, fetchMore, refetch } = useQuery(
    LIST_FINANCE_QUOTE_ACTIVITY_LOGS_QUERY,
    {
      skip: !quoteId || skip,
      variables: {
        quoteId,
        params: { limit: 20, skip: 0, sort: ["created_at:desc"] },
      },
      fetchPolicy: "cache-and-network",
    },
  );
  const result = data?.listFinanceQuoteActivityLogs;
  return {
    activityLogs: result?.logs ?? [],
    totalCount: result?.totalCount ?? 0,
    loading,
    fetchMore,
    refetch,
  };
};

export const useListOemEmailThreadsByFinanceQuoteLazy = () => {
  const [listThreads, { loading, refetch }] = useLazyQuery(
    LIST_OEM_EMAIL_THREADS_BY_FINANCE_QUOTE_QUERY,
    { fetchPolicy: "network-only" },
  );

  const fetchThreads = useCallback(
    async (quoteId: string, emailAddressId: string) => {
      const result = await listThreads({
        variables: { quoteId, emailAddressId },
      });
      return (result.data?.listOemEmailThreadsByFinanceQuote ??
        []) as unknown[];
    },
    [listThreads],
  );

  return { fetchThreads, loading, refetch };
};

export const useAttachFinanceQuotePurchaseOrder = () => {
  const [mutate, { loading }] = useMutation(
    ATTACH_FINANCE_QUOTE_PURCHASE_ORDER_MUTATION,
  );
  return {
    attachFinanceQuotePurchaseOrder: (
      quoteId: string,
      purchaseOrder: {
        name?: string;
        url: string;
        type?: string;
        size?: string;
      },
    ) =>
      new Promise<FinanceQuote>((resolve, reject) => {
        mutate({
          variables: { quoteId, purchaseOrder },
          refetchQueries: financeQuoteDetailRefetches(quoteId),
          update: (cache) => {
            evictFinanceQuoteActivityLogs(cache);
          },
        })
          .then(({ data }) => resolve(data?.attachFinanceQuotePurchaseOrder))
          .catch((err) => reject(errorsHandler(err)));
      }),
    loading,
  };
};

export const useRemoveFinanceQuotePurchaseOrder = () => {
  const [mutate, { loading }] = useMutation(
    REMOVE_FINANCE_QUOTE_PURCHASE_ORDER_MUTATION,
  );
  return {
    removeFinanceQuotePurchaseOrder: (quoteId: string, url?: string) =>
      new Promise<FinanceQuote>((resolve, reject) => {
        mutate({
          variables: { quoteId, url },
          refetchQueries: financeQuoteDetailRefetches(quoteId),
          update: (cache) => {
            evictFinanceQuoteActivityLogs(cache);
          },
        })
          .then(({ data }) => resolve(data?.removeFinanceQuotePurchaseOrder))
          .catch((err) => reject(errorsHandler(err)));
      }),
    loading,
  };
};
